import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { sdk } from '@/services/sdk';
import { useAuth } from '@/layouts/RootLayout';
import { useFetch } from '@/hooks/useFetch';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Field } from '@/components/kit';
import { FormDialog, PickSelect } from '@/components/inputs';
import { summarise } from '@/components/tickets/Thread';
import { TICKET_CATEGORIES, TICKET_PRIORITIES } from '@/shared/constants';
import { myOrders } from '@/shared/account';
import { committed, idOf, nowIso } from '@/shared/data';
import { planSummary } from '@/shared/pricing';

const NONE = 'none';

function blank(orderId) {
  return {
    Name: '',
    order_c: orderId ? String(orderId) : NONE,
    category_c: 'Setup',
    priority_c: 'Normal',
    body: '',
  };
}

function fromTicket(t) {
  const order = idOf(t.order_c);
  return {
    Name: t.Name ?? '',
    order_c: order ? String(order) : NONE,
    category_c: t.category_c || 'Other',
    priority_c: t.priority_c || 'Normal',
    body: '',
  };
}

/** Open a ticket (with its first message) or edit one the customer already opened. */
export default function TicketDialog({ open, onOpenChange, ticket, defaultOrder, onSaved }) {
  const { user } = useAuth();
  const editing = !!ticket;
  const [form, setForm] = useState(blank(defaultOrder));
  const [saving, setSaving] = useState(false);
  const orders = useFetch(
    () => (open ? myOrders(['Name', 'plan_c', 'term_c', 'connections_c', 'status_c']) : Promise.resolve([])),
    [open],
  );
  const orderList = orders.data ?? [];
  const orderKey = Object.fromEntries(orderList.map((o) => [String(o.Id), o.Id]));
  const orderLabels = Object.fromEntries(orderList.map((o) => [String(o.Id), `${o.Name} · ${planSummary(o)}`]));

  useEffect(() => {
    if (open) setForm(ticket ? fromTicket(ticket) : blank(defaultOrder));
  }, [open]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  async function submit() {
    const subject = form.Name.trim();
    const body = form.body.trim();
    if (!subject) {
      toast.error('Add a subject so we know what it is about');
      return;
    }
    if (!editing && !body) {
      toast.error('Tell us what is going on');
      return;
    }
    const orderId = form.order_c === NONE ? null : (orderKey[form.order_c] ?? idOf(ticket?.order_c) ?? form.order_c);
    const fields = { Name: subject, category_c: form.category_c, priority_c: form.priority_c };

    setSaving(true);
    try {
      if (editing) {
        const res = await sdk.table('tickets_c').update({ Id: ticket.Id, ...fields, order_c: orderId });
        if (!committed(res, 'Ticket updated')) return;
        onOpenChange(false);
        onSaved?.();
        return;
      }

      const res = await sdk.table('tickets_c').create({
        ...fields,
        ...(orderId ? { order_c: orderId } : {}),
        email_c: user?.emailAddress ?? '',
        status_c: 'Open',
        last_reply_at_c: nowIso(),
      });
      const created = committed(res);
      if (!created) return;
      const msg = await sdk.table('ticket_messages_c').create({
        Name: summarise(body),
        ticket_c: created.Id,
        author_c: 'Customer',
        body_c: body,
      });
      committed(msg, 'Ticket opened. We usually reply within a few hours.');
      onOpenChange(false);
      setForm(blank());
      onSaved?.(created);
    } finally {
      setSaving(false);
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? 'Edit ticket' : 'Open a support ticket'}
      description={editing ? undefined : 'A real person reads every ticket. Include what you tried and what you saw.'}
      onSubmit={submit}
      saving={saving}
      submitLabel={editing ? 'Save changes' : 'Open ticket'}
      busyLabel={editing ? 'Saving…' : 'Opening…'}
    >
      <Field label="Subject" htmlFor="t-subject">
        <Input
          id="t-subject"
          value={form.Name}
          onChange={(e) => set('Name')(e.target.value)}
          placeholder="Channels freeze after a few minutes"
        />
      </Field>
      <Field label="Order" htmlFor="t-order" hint="Optional. Pick the order this is about.">
        <PickSelect
          id="t-order"
          value={form.order_c}
          onChange={set('order_c')}
          values={[NONE, ...orderList.map((o) => String(o.Id))]}
          labels={{ [NONE]: 'Not about a specific order', ...orderLabels }}
          placeholder={orders.loading ? 'Loading your orders…' : 'Choose an order'}
        />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Category" htmlFor="t-category">
          <PickSelect
            id="t-category"
            value={form.category_c}
            onChange={set('category_c')}
            values={TICKET_CATEGORIES}
            placeholder="Choose a category"
          />
        </Field>
        <Field label="Priority" htmlFor="t-priority">
          <PickSelect
            id="t-priority"
            value={form.priority_c}
            onChange={set('priority_c')}
            values={TICKET_PRIORITIES}
            placeholder="How urgent is it"
          />
        </Field>
      </div>
      {!editing && (
        <Field label="Message" htmlFor="t-body">
          <Textarea
            id="t-body"
            rows={5}
            value={form.body}
            onChange={(e) => set('body')(e.target.value)}
            placeholder="Which device, which app, what happens and when it started…"
          />
        </Field>
      )}
    </FormDialog>
  );
}
